/**
 * ElevateGovernanceAdmin - S5-04 content governance for the Elevate module.
 *
 * Three surfaces, all reading the migration 262 RPCs:
 *   * Review queue   — objects per pack, PRS 8.3 lifecycle, GOV-07 and GOV-09
 *                      markers, with the approval and publication actions.
 *   * Publication    — the CP-03 verification set per competency and level with
 *                      its approval evidence (approver, timestamp, version).
 *   * Gate counts    — published variants per competency in Framework Listing
 *                      Order, against the EC-07 Gate 3 / Gate 4 figures.
 *
 * TWO PATHS, BOTH VALID. The HP-1B CLIs (scripts/approve-content.ts and
 * scripts/publish-content.ts) remain the operational route for wave runs with
 * service-role credentials and a named approver flag. This screen is the same
 * two database acts performed by a signed-in person. They share the migration
 * 237 guards, the same approval records and the same audit trail; neither is a
 * back door around the other.
 *
 * Permissions: the page is behind SuperAdminLayout (WingMentor administrator).
 * ACTING on the queue additionally needs the WingMentor-approver grant
 * (GOV-02, DEC-09) — the elevate.content_approver_grants table is the
 * authority, and a WingMentor administrator who does not hold a grant is
 * refused by the database with its own named error.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, RefreshCw, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useAirline } from '../../contexts/AirlineContext';
import ContentReviewQueue from '../../components/admin/elevate/ContentReviewQueue';
import { GuardRefusal, TestOnlyBadge } from '../../components/admin/elevate/ContentBadges';
import {
  summariseGateProgress,
  summariseTestOnlyExposure,
  DEVELOPMENT_LEVEL_LABELS,
  LIFECYCLE_LABELS,
  type ContentLifecycleStatus,
  type ReviewQueueRow,
} from '../../lib/elevateGovernance';
import {
  approveContentObject,
  fetchContentApprovers,
  fetchGateCounts,
  fetchGateTargets,
  fetchIsContentApprover,
  fetchPublicationVerification,
  fetchReviewQueue,
  publishContentObject,
} from '../../lib/elevateGovernanceApi';

type Panel = 'queue' | 'publication' | 'gates';

const STATUS_FILTERS: Array<{ value: ContentLifecycleStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All states' },
  ...(
    ['draft', 'technical_review', 'educational_review', 'awaiting_approval', 'approved', 'published', 'under_review', 'superseded', 'archived'] as ContentLifecycleStatus[]
  ).map((value) => ({ value, label: LIFECYCLE_LABELS[value] })),
];

export default function ElevateGovernanceAdmin() {
  const { isSuperAdmin } = useAirline();
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<Panel>('queue');
  const [statusFilter, setStatusFilter] = useState<ContentLifecycleStatus | 'all'>('draft');
  /** S5-05: an auditor's question is "what exactly carries the GOV-09 marking?" */
  const [testOnlyOnly, setTestOnlyOnly] = useState(false);
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const [outcomes, setOutcomes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const { data: isApprover } = useQuery({
    queryKey: ['elevate-governance', 'is-approver'],
    queryFn: fetchIsContentApprover,
    enabled: isSuperAdmin,
  });

  const { data: approvers } = useQuery({
    queryKey: ['elevate-governance', 'approvers'],
    queryFn: fetchContentApprovers,
    enabled: isSuperAdmin,
  });

  const queueQuery = useQuery({
    queryKey: ['elevate-governance', 'queue', statusFilter],
    queryFn: () => fetchReviewQueue({ status: statusFilter === 'all' ? null : statusFilter }),
    enabled: isSuperAdmin,
  });

  const publicationQuery = useQuery({
    queryKey: ['elevate-governance', 'publication'],
    queryFn: () => fetchPublicationVerification(null),
    enabled: isSuperAdmin && panel === 'publication',
  });

  const gateCountsQuery = useQuery({
    queryKey: ['elevate-governance', 'gate-counts'],
    queryFn: fetchGateCounts,
    enabled: isSuperAdmin && panel === 'gates',
  });

  const gateTargetsQuery = useQuery({
    queryKey: ['elevate-governance', 'gate-targets'],
    queryFn: fetchGateTargets,
    enabled: isSuperAdmin && panel === 'gates',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['elevate-governance'] });
  };

  /** Both actions share this shape: clear the row's previous result, run, keep
   *  whatever came back — a refusal is as much a result as a success. */
  const runAction = async (
    row: ReviewQueueRow,
    action: (uuid: string) => Promise<{ object_id: string; status_before: string; status_after: string; content_version: string }>,
  ) => {
    setBusy(row.object_uuid);
    setRefusals((prev) => ({ ...prev, [row.object_uuid]: '' }));
    setOutcomes((prev) => ({ ...prev, [row.object_uuid]: '' }));
    try {
      const result = await action(row.object_uuid);
      setOutcomes((prev) => ({
        ...prev,
        [row.object_uuid]: `${result.object_id} v${result.content_version}: ${result.status_before} → ${result.status_after}`,
      }));
      invalidate();
    } catch (error) {
      setRefusals((prev) => ({ ...prev, [row.object_uuid]: (error as Error).message }));
    } finally {
      setBusy(null);
    }
  };

  const approveMutation = useMutation({
    mutationFn: (row: ReviewQueueRow) => runAction(row, approveContentObject),
  });
  const publishMutation = useMutation({
    mutationFn: (row: ReviewQueueRow) => runAction(row, publishContentObject),
  });

  const gateProgress = useMemo(
    () => summariseGateProgress(gateCountsQuery.data ?? [], gateTargetsQuery.data ?? []),
    [gateCountsQuery.data, gateTargetsQuery.data],
  );

  const exposure = useMemo(
    () => summariseTestOnlyExposure(queueQuery.data ?? []),
    [queueQuery.data],
  );
  const queueRows = useMemo(() => exposure.visible(testOnlyOnly), [exposure, testOnlyOnly]);

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need super admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Elevate Content Governance</h1>
          <p className="mt-1 text-sm text-gray-600">
            Content lifecycle, approval and publication for the WingMentor library (PRS 8.3, GOV-01 to GOV-09).
          </p>
        </div>
        <button
          onClick={invalidate}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Who is acting, and under whose authority (GOV-02, DEC-09). */}
      <div
        className={`mb-4 flex items-start gap-2 px-4 py-3 rounded-md border text-sm ${
          isApprover
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
            : 'border-amber-300 bg-amber-50 text-amber-900'
        }`}
      >
        {isApprover ? (
          <ShieldCheck className="w-5 h-5 flex-shrink-0" />
        ) : (
          <ShieldAlert className="w-5 h-5 flex-shrink-0" />
        )}
        <div>
          {isApprover ? (
            <p>
              You hold the WingMentor-approver role. Approval binds your identity to the exact content
              version (GOV-02, GOV-03).
            </p>
          ) : (
            <p>
              Read-only: you do not hold the WingMentor-approver role, so approval and publication are
              refused (GOV-02, DEC-09). Administering the role stays a service-role action.
            </p>
          )}
          {approvers && approvers.length > 0 && (
            <p className="mt-1 text-xs opacity-80">
              Approver grants on record:{' '}
              {approvers.map((a) => `${a.full_name ?? a.user_id} (${a.email ?? 'no email'})`).join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* Panels */}
      <nav className="flex gap-1 mb-4" aria-label="Governance panels">
        {(
          [
            ['queue', 'Review queue'],
            ['publication', 'Publication verification'],
            ['gates', 'Gate 3 / Gate 4 counts'],
          ] as Array<[Panel, string]>
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setPanel(value)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border ${
              panel === value
                ? 'bg-ice text-wm-deep-blue border-wm-deep-blue'
                : 'text-gray-600 border-transparent hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {panel === 'queue' && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <label htmlFor="lifecycle-filter" className="text-sm text-gray-600">
              Lifecycle state
            </label>
            <select
              id="lifecycle-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ContentLifecycleStatus | 'all')}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>

            <label className="flex items-center gap-2 text-sm text-gray-600 ml-4">
              <input
                type="checkbox"
                checked={testOnlyOnly}
                onChange={(e) => setTestOnlyOnly(e.target.checked)}
              />
              Test-only content only
              <span className="text-xs text-gray-500">
                ({exposure.testOnly} of {exposure.total} in this view)
              </span>
            </label>
          </div>

          <p className="mb-3 text-xs text-gray-500">
            GOV-09: test-only content is publishable to the designated test tenant and is excluded from any
            production release. The database enforces that on the trainee side (migrations 251 and 263); the
            badge is here so the person approving and publishing sees the marking before they act.
          </p>

          {queueQuery.isLoading && <div className="p-8 text-center text-gray-500">Loading the review queue…</div>}
          {queueQuery.error && <GuardRefusal message={(queueQuery.error as Error).message} />}
          {queueQuery.data && (
            <ContentReviewQueue
              rows={queueRows}
              isApprover={isApprover === true}
              busyObjectUuid={busy}
              refusals={refusals}
              outcomes={outcomes}
              onApprove={(row) => approveMutation.mutate(row)}
              onPublish={(row) => publishMutation.mutate(row)}
            />
          )}
        </>
      )}

      {panel === 'publication' && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
          {publicationQuery.error && <GuardRefusal message={(publicationQuery.error as Error).message} />}
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Competency</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Object</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approved by</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approved at</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Framework</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(publicationQuery.data ?? []).map((row) => (
                <tr key={`${row.content_pack_id}-${row.object_id}-${row.version}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-900">{row.competency_code}</td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {DEVELOPMENT_LEVEL_LABELS[row.development_level] ?? row.development_level}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    <span className="block">{row.title}</span>
                    <span className="block font-mono text-gray-500">{row.object_id}</span>
                    {row.test_only && (
                      <span className="inline-block mt-1">
                        <TestOnlyBadge compact />
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs font-mono text-gray-900">{row.approved_version}</td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    <span className="block">{row.approver_name}</span>
                    <span className="block text-gray-500">{row.approver_email}</span>
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-700">{row.approved_at}</td>
                  <td className="px-2 py-2 text-xs font-mono text-gray-500">{row.authored_framework_version}</td>
                </tr>
              ))}
              {(publicationQuery.data ?? []).length === 0 && !publicationQuery.isLoading && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                    Nothing is published yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {panel === 'gates' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {gateProgress.map((g) => (
              <div
                key={g.gate}
                className={`p-4 rounded-lg border ${
                  g.met ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <BadgeCheck className={`w-5 h-5 ${g.met ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {g.gate} — {g.milestoneDate}
                  </h3>
                </div>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {g.published}
                  <span className="text-base font-normal text-gray-500"> / {g.target} variants</span>
                </p>
                <p className="mt-1 text-xs text-gray-500">{g.source}</p>
              </div>
            ))}
          </div>

          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Competency</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Published variants</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Core</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Supplements</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Levels covered</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Test-only (excluded)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(gateCountsQuery.data ?? []).map((row) => (
                  <tr key={row.competency_code} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-900">
                      <span className="font-mono font-medium">{row.competency_code}</span>
                      <span className="block text-gray-500">{row.competency_title}</span>
                    </td>
                    <td className="px-2 py-2 text-xs text-right font-semibold text-gray-900">{row.published_variants}</td>
                    <td className="px-2 py-2 text-xs text-right text-gray-700">{row.published_core}</td>
                    <td className="px-2 py-2 text-xs text-right text-gray-700">{row.published_supplements}</td>
                    <td className="px-2 py-2 text-xs text-right text-gray-700">{row.levels_covered} / 3</td>
                    <td className="px-2 py-2 text-xs text-right text-amber-800">{row.test_only_variants}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            Test-only variants are counted apart and never inside a gate total: GOV-09 excludes them from any
            production release, so counting them would overstate readiness by exactly the material that must not
            ship.
          </p>
        </div>
      )}
    </div>
  );
}
